import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Home, BarChart2, Settings, LogOut, Banknote,
  FileText, ChevronRight, CookingPot, Sofa, Wallet,
  Receipt, ChevronDown, Keyboard, ChevronUp, Sun, Moon,
  HandHelping, MessageSquareLock, UtensilsCrossed, Plus, Menu, Clock, History
} from "lucide-react";
import { useAuth } from '../../common/context/AuthContext'; 

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut, userData } = useAuth(); 
  const restrictedList = userData?.restrictedFeatures || [];
  const isManager = userData?.role === 'manager';

  const [isReportsOpen, setIsReportsOpen] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(
    () => localStorage.getItem("sidebarCollapsed") === "true"
  );
  const [isDarkTheme, setIsDarkTheme] = useState(() => {
    const saved = localStorage.getItem("theme");
    return saved === "dark";
  });

  const restaurantName = "TOBO Billing";
  const version = "v1.2.5 • Pro";

  /* ---------------- theme & layout ---------------- */
  useEffect(() => {
    localStorage.setItem("theme", isDarkTheme ? "dark" : "light");
    document.documentElement.classList.toggle("dark", isDarkTheme);
  }, [isDarkTheme]);

  useEffect(() => {
    localStorage.setItem("sidebarCollapsed", isCollapsed.toString());
  }, [isCollapsed]);

  /* ---------------- helpers ---------------- */
  const isBillGenerationPage = () =>
    location.pathname.includes("/generate-bill") ||
    location.pathname.includes("/walkin-bill") ||
    location.pathname.includes("/saved-bill") ||
    location.pathname.includes("/print-kot") ||
    location.pathname.includes("/print-kot2");

  // FIXED: Properly logout with auth context
  const handleLogout = async () => {
    try {
      await signOut();
      navigate("/signin");
    } catch (error) {
      console.error('Logout failed:', error);
      // Still navigate to signin even if signOut fails
      navigate("/signin");
    }
  };

  const renderNavItem = ({ name, icon: Icon, path }) => {
    const isActive = location.pathname === path;
    return (
      <Link
        key={name}
        to={path}
        onClick={() => {
          // Close mobile sidebar on navigation (mobile only)
          if (window.innerWidth < 1024) {
            setIsMobileOpen(false);
          }
        }}
        className={`flex items-center px-4 py-2.5 rounded-lg transition-colors ${
          isActive
            ? "bg-[#E0E7FF] text-[#4F46E5] font-semibold"
            : isDarkTheme 
              ? "text-slate-400 hover:bg-slate-800" 
              : "text-slate-500 hover:bg-slate-100"
        }`}
      >
        <Icon size={20} />
        {!isCollapsed && <span className="ml-3 text-sm">{name}</span>}
      </Link>
    );
  };

  const renderSubNavItem = ({ name, icon: Icon, path }) => {
    const isActive = location.pathname === path;
    return (
      <Link
        key={name}
        to={path}
        onClick={() => {
          // Close mobile sidebar on navigation (mobile only)
          if (window.innerWidth < 1024) {
            setIsMobileOpen(false);
          }
        }}
        className={`flex items-center gap-3 pl-12 pr-4 py-2 rounded-lg transition-colors text-xs font-medium ${
          isActive
            ? "text-[#4F46E5] font-bold"
            : isDarkTheme
              ? "text-slate-400 hover:text-slate-300 hover:bg-slate-800"
              : "text-slate-400 hover:text-slate-700 hover:bg-slate-50"
        }`}
      >
        <div className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-[#4F46E5]" : isDarkTheme ? "bg-slate-600" : "bg-slate-200"}`}></div>
        <span>{name}</span>
      </Link>
    );
  };

  /* ---------------- navigation data ---------------- */
  const sidebarItems = [
    { name: "Dashboard", icon: Home, path: "/dashboard" },
    { name: "Menu", icon: CookingPot, path: "/product" },
    { name: "Daily Expenses", icon: Wallet, path: "/expense" },
  ].filter(item => {
    if (isManager) {
      if (item.name === "Menu" && restrictedList.includes("inventory")) return false;
      if (item.name === "Daily Expenses" && restrictedList.includes("expense_reports")) return false;
    }
    return true;
  });

  const quickNavItems = [
    { name: "New Bill", icon: Keyboard, path: "/new-bill" },
    { name: "KOT Display", icon: FileText, path: "/kot" },
    { name: "Table Activity", icon: Sofa, path: "/table-manager" },
  ].filter(item => {
    if (isManager) {
      if (item.name === "New Bill" && restrictedList.includes("billing")) return false;
      if (item.name === "KOT Display" && restrictedList.includes("billing")) return false;
      if (item.name === "Table Activity" && restrictedList.includes("inventory")) return false;
    }
    return true;
  });

  const reportsSubItems = [
    { name: "Expense Report", icon: Wallet, path: "/expense-report" },
    { name: "Sales Report", icon: FileText, path: "/sales-report" },
    { name: "GST Report", icon: Receipt, path: "/gst-report" },
    { name: "Peak Hours Analytics", icon: Clock, path: "/peak-hours" },
  ].filter(item => {
    if (isManager) {
      if (item.name === "Expense Report" && restrictedList.includes("expense_reports")) return false;
      if (item.name === "Sales Report" && restrictedList.includes("sales_reports")) return false;
      if (item.name === "GST Report" && restrictedList.includes("gst_reports")) return false;
      if (item.name === "Peak Hours Analytics" && restrictedList.includes("analytics")) return false;
    }
    return true;
  });

  const quickActions = [
    { name: "Settings", icon: Settings, path: "/settings" },
    { name: "Bill History", icon: History, path: "/bill-history" },
    { name: "Subscription Info", icon: Banknote, path: "/ViewSubscriptionInfo" },
  ].filter(item => {
    if (isManager) {
      if (item.name === "Settings" && restrictedList.includes("settings")) return false;
      if (item.name === "Bill History" && restrictedList.includes("billing")) return false;
    }
    return true;
  });

  const supportItems = [
    { name: "Support", icon: HandHelping, path: "/support" },
    { name: "View Support", icon: MessageSquareLock, path: "/view-support" },
  ];

  const SectionTitle = ({ children }) => (
    !isCollapsed && (
      <div className="text-[10px] font-bold text-slate-400 tracking-wider uppercase px-4 mt-8 mb-2">
        {children}
      </div>
    )
  );

  const Section = ({ title, items }) => (
    <div className="mb-2">
      <SectionTitle>{title}</SectionTitle>
      <div className="space-y-1">{items.map(renderNavItem)}</div>
    </div>
  );

  /* ---------------- render ---------------- */
  return (
    <>
      {/* Mobile menu button */}
      {!isBillGenerationPage() && (
        <button
          className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-lg hover:shadow-xl transition-shadow"
          onClick={() => setIsMobileOpen(!isMobileOpen)}
          aria-label="Toggle menu"
        >
          {isMobileOpen ? (
            <ChevronRight className="w-5 h-5 text-gray-700" />
          ) : (
            <Menu className="w-5 h-5 text-gray-700" />
          )}
        </button>
      )}

      {/* Mobile overlay */}
      {isMobileOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      <aside
        className={`fixed lg:sticky top-0 left-0 h-screen z-40 transition-all border-r flex flex-col
          ${isDarkTheme ? "bg-slate-900 border-slate-800 text-slate-200" : "bg-white border-slate-200 text-slate-900"}
          ${isCollapsed ? "w-14" : "w-64"}
          ${isMobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
      >
        {/* Header/Brand */}
        <div className="p-6 flex items-center gap-3">
          {!isCollapsed ? (
            <>
              <div className="w-10 h-10 bg-[#4F46E5] rounded-xl flex items-center justify-center text-white">
                <UtensilsCrossed size={24} />
              </div>
              <div className="flex-1">
                <h1 className={`font-bold leading-tight ${isDarkTheme ? "text-slate-100" : "text-slate-900"}`}>{restaurantName}</h1>
                <p className="text-xs text-slate-400 font-medium">{version}</p>
              </div>
              <button 
                onClick={() => setIsDarkTheme(!isDarkTheme)}
                className={`transition-colors ${isDarkTheme ? "text-slate-400 hover:text-slate-300" : "text-slate-400 hover:text-slate-600"}`}
                aria-label="Toggle theme"
              >
                {isDarkTheme ? <Sun size={18} /> : <Moon size={18} />}
              </button>
            </>
          ) : (
            <div className="flex flex-col items-center gap-2 w-full">
              <div className="w-8 h-8 bg-[#4F46E5] rounded-lg flex items-center justify-center text-white">
                <UtensilsCrossed size={20} />
              </div>
              <button 
                onClick={() => setIsDarkTheme(!isDarkTheme)}
                className={`transition-colors ${isDarkTheme ? "text-slate-400 hover:text-slate-300" : "text-slate-400 hover:text-slate-600"}`}
                aria-label="Toggle theme"
              >
                {isDarkTheme ? <Sun size={16} /> : <Moon size={16} />}
              </button>
            </div>
          )}
        </div>

        {/* Quick Action Button */}
        {!isCollapsed && !restrictedList.includes("billing") && (
          <div className="px-4 mb-2">
            <Link
              to="/new-bill"
              onClick={() => {
                // Close mobile sidebar on navigation (mobile only)
                if (window.innerWidth < 1024) {
                  setIsMobileOpen(false);
                }
              }}
              className="w-full flex items-center justify-center gap-2 bg-[#F1F5F9] text-slate-900 py-2.5 rounded-xl font-semibold hover:bg-slate-200 transition-colors text-sm border border-slate-100"
            >
              <Plus size={18} />
              Quick Bill
            </Link>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 px-2 pb-8 overflow-y-auto">
          <Section title="Main Navigation" items={sidebarItems} />
          <Section title="Quick Navigation" items={quickNavItems} />

          {/* Reports */}
          {reportsSubItems.length > 0 && (
            <div className="mb-2">
              <SectionTitle>Reports & Analytics</SectionTitle>
            <button
              onClick={() => setIsReportsOpen(!isReportsOpen)}
              className={`flex items-center justify-between w-full px-4 py-2.5 rounded-lg transition-colors ${
                location.pathname.includes("/expense-report") ||
                location.pathname.includes("/sales-report") ||
                location.pathname.includes("/gst-report")
                  ? "bg-[#E0E7FF] text-[#4F46E5] font-semibold"
                  : isDarkTheme
                    ? "text-slate-400 hover:bg-slate-800"
                    : "text-slate-500 hover:bg-slate-100"
              }`}
            >
              <div className="flex items-center">
                <BarChart2 size={20} />
                {!isCollapsed && <span className="ml-3 text-sm">Reports</span>}
              </div>
              {!isCollapsed && (
                isReportsOpen ? <ChevronUp size={14} className="opacity-50" /> : <ChevronDown size={14} className="opacity-50" />
              )}
            </button>

            {isReportsOpen && !isCollapsed && (
              <div className="mt-1 space-y-1 animate-in slide-in-from-top-2 duration-200">
                {reportsSubItems.map(renderSubNavItem)}
              </div>
            )}
            </div>
          )}

          <Section title="Quick Actions" items={quickActions} />
          <Section title="Support" items={supportItems} />
        </nav>

        {/* Collapse Toggle (Desktop only) */}
        <div className={`hidden lg:block px-4 pb-2 border-t ${isDarkTheme ? "border-slate-800" : "border-slate-100"}`}>
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className={`flex items-center justify-center w-full px-4 py-2 mt-2 text-sm rounded-lg transition-colors ${
              isDarkTheme 
                ? "text-slate-400 hover:bg-slate-800 hover:text-slate-300" 
                : "text-slate-500 hover:bg-slate-100 hover:text-slate-700"
            }`}
            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {!isCollapsed && <span className="mr-2">Collapse</span>}
            <ChevronRight 
              size={16} 
              className={`transition-transform ${isCollapsed ? "" : "rotate-180"}`}
            />
          </button>
        </div>

        {/* Logout */}
        <div className={`p-4 border-t ${isDarkTheme ? "border-slate-800" : "border-slate-100"}`}>
          <button
            onClick={handleLogout}
            className={`flex items-center gap-3 w-full px-4 py-3 text-red-500 hover:text-red-600 transition-colors text-sm font-medium rounded-lg ${isDarkTheme ? "hover:bg-red-900/20" : "hover:bg-red-50"}`}
          >
            <LogOut size={20} />
            {!isCollapsed && <span>Logout</span>}
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;